<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import AddTelegram from '@/views/settings/inboxes/AddTelegram.vue'
import AddMax from '@/views/settings/inboxes/AddMax.vue'
import AddWebWidget from '@/views/settings/inboxes/AddWebWidget.vue'
import AddVk from '@/views/settings/inboxes/AddVk.vue'
import { useAlert } from '@/composables/useToast'
import ChannelIcon from '@/components/ChannelIcon.vue'

const store = useStore()
const { t } = useI18n()

const showAddModal = ref(false)
const selectedChannel = ref(null)
const showDeleteModal = ref(false)
const selectedInbox = ref(null)

const inboxList = computed(() => store.getters['inboxes/getInboxes'])
const uiFlags = computed(() => store.getters['inboxes/getUIFlags'])

const channelLabel = inbox =>
  t(`INBOX_MGMT.CHANNELS.${inbox.channel_slug.toUpperCase()}`, inbox.channel_slug)

const openAdd = () => {
  selectedChannel.value = null
  showAddModal.value = true
}

const closeAdd = () => {
  showAddModal.value = false
  selectedChannel.value = null
}

const addModalTitle = computed(() => {
  if (selectedChannel.value === 'telegram') return t('INBOX_MGMT.ADD.TELEGRAM.TITLE')
  if (selectedChannel.value === 'max') return t('INBOX_MGMT.ADD.MAX.TITLE')
  if (selectedChannel.value === 'web_widget')
    return t('INBOX_MGMT.ADD.WEB_WIDGET.TITLE')
  if (selectedChannel.value === 'vk') return t('INBOX_MGMT.ADD.VK.TITLE')
  return t('INBOX_MGMT.ADD.PICK.TITLE')
})

const openDelete = inbox => {
  selectedInbox.value = inbox
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  try {
    await store.dispatch('inboxes/delete', selectedInbox.value.id)
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS'))
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR'))
  } finally {
    showDeleteModal.value = false
  }
}

onMounted(() => {
  store.dispatch('inboxes/get')
})
</script>

<template>
  <div
    class="flex flex-col w-full h-full px-6 pt-4 pb-8 m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full mx-auto max-w-5xl">
      <div class="flex flex-col w-full h-full gap-4 font-inter">
        <div class="flex flex-col gap-2">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ $t('INBOX_MGMT.HEADER') }}
          </h1>
          <p class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex items-center justify-end">
          <Button
            sm
            :label="$t('INBOX_MGMT.ADD.BUTTON')"
            icon="i-lucide-plus"
            @click="openAdd"
          />
        </div>

        <div>
          <div
            v-if="uiFlags.isFetching"
            class="flex items-center justify-center py-20 text-body-main text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.LOADING') }}
          </div>

          <span
            v-else-if="!inboxList.length"
            class="flex items-center justify-center py-20 text-body-main !text-base text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.LIST.404') }}
          </span>

          <div v-else class="border-t divide-y divide-n-weak border-n-weak">
            <div
              v-for="inbox in inboxList"
              :key="inbox.id"
              class="flex flex-row items-center justify-between gap-4 py-4"
            >
              <div class="flex items-center gap-4">
                <ChannelIcon
                  :slug="inbox.channel_slug"
                  icon-class="size-10"
                  image-class="size-10"
                  class="shrink-0"
                />
                <div class="flex flex-col items-start gap-1.5">
                  <span class="block text-heading-3 text-n-slate-12">
                    {{ inbox.name }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-body-main text-n-slate-11">
                      {{ channelLabel(inbox) }}
                    </span>
                    <template v-if="inbox.channel?.bot_name">
                      <div class="w-px h-3 rounded-lg bg-n-strong" />
                      <span class="text-body-main text-n-slate-11">
                        {{ '@' + inbox.channel.bot_name }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS.BUTTON')"
                    slate
                    sm
                    icon="i-mk-settings"
                    :aria-label="$t('INBOX_MGMT.SETTINGS.BUTTON')"
                  />
                </router-link>
                <Button
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON')"
                  slate
                  sm
                  icon="i-mk-bin"
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :aria-label="$t('INBOX_MGMT.DELETE.BUTTON')"
                  @click="openDelete(inbox)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal :show="showAddModal" :title="addModalTitle" @close="closeAdd">
      <div v-if="!selectedChannel" class="flex flex-col gap-3">
        <p class="text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.PICK.DESC') }}
        </p>
        <button
          type="button"
          class="flex items-center gap-3 p-3 text-left border rounded-xl border-n-weak hover:bg-n-alpha-2"
          @click="selectedChannel = 'telegram'"
        >
          <ChannelIcon
            slug="telegram"
            icon-class="size-10"
            image-class="size-10"
            class="shrink-0"
          />
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.CHANNELS.TELEGRAM') }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-3 p-3 text-left border rounded-xl border-n-weak hover:bg-n-alpha-2"
          @click="selectedChannel = 'max'"
        >
          <ChannelIcon
            slug="max"
            icon-class="size-10"
            image-class="size-10"
            class="shrink-0"
          />
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.CHANNELS.MAX') }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-3 p-3 text-left border rounded-xl border-n-weak hover:bg-n-alpha-2"
          @click="selectedChannel = 'web_widget'"
        >
          <ChannelIcon
            slug="web_widget"
            icon-class="size-10"
            image-class="size-10"
            class="shrink-0"
          />
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.CHANNELS.WEB_WIDGET') }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-3 p-3 text-left border rounded-xl border-n-weak hover:bg-n-alpha-2"
          @click="selectedChannel = 'vk'"
        >
          <ChannelIcon
            slug="vk"
            icon-class="size-10"
            image-class="size-10"
            class="shrink-0"
          />
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.CHANNELS.VK') }}
          </span>
        </button>
      </div>

      <AddTelegram
        v-else-if="selectedChannel === 'telegram'"
        @close="closeAdd"
      />
      <AddMax v-else-if="selectedChannel === 'max'" @close="closeAdd" />
      <AddWebWidget
        v-else-if="selectedChannel === 'web_widget'"
        @close="closeAdd"
      />
      <AddVk v-else-if="selectedChannel === 'vk'" @close="closeAdd" />
    </Modal>

    <Modal
      :show="showDeleteModal"
      :title="$t('INBOX_MGMT.DELETE.TITLE')"
      @close="showDeleteModal = false"
    >
      <div class="flex flex-col gap-6">
        <p class="text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.DELETE.CONFIRM', { name: selectedInbox?.name }) }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            :label="$t('INBOX_MGMT.DELETE.CANCEL')"
            @click="showDeleteModal = false"
          />
          <Button
            ruby
            :label="$t('INBOX_MGMT.DELETE.SUBMIT')"
            :disabled="uiFlags.isDeleting"
            @click="confirmDelete"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>
